import axios from 'axios';
import config from '@/config';

const API_URL = config.BACKEND_URL;

// Log the API URL being used (helpful for debugging)
console.log('[API] Using backend URL:', API_URL);

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  // No default Content-Type — Axios 1.x auto-detects from data type:
  // Objects → application/json, FormData → multipart/form-data (with boundary)
});

// EMPLOYER NAME PRIVACY: Sanitize all API responses to replace
// employer personal names with business_name. Defense-in-depth layer
// that works alongside the backend middleware.
function sanitizeEmployerNamesInResponse(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    obj.forEach(sanitizeEmployerNamesInResponse);
    return obj;
  }
  if ((obj.role === 'employer' || obj.role === 'owner') && 'name' in obj) {
    obj.name = obj.business_name || 'Employer';
  }
  for (const key of ['employer', 'owner']) {
    if (obj[key] && typeof obj[key] === 'object' && 'name' in obj[key]) {
      obj[key].name = obj[key].business_name || 'Employer';
    }
  }
  for (const val of Object.values(obj)) {
    if (val && typeof val === 'object') sanitizeEmployerNamesInResponse(val);
  }
  return obj;
}

api.interceptors.response.use((response) => {
  // Skip sanitization on admin pages — admins need real employer names
  if (window.location.pathname.startsWith('/admin')) {
    return response;
  }
  if (response.data && typeof response.data === 'object') {
    sanitizeEmployerNamesInResponse(response.data);
  }
  return response;
});

/** Longer timeout for file uploads (60s instead of the default 15s). */
export const UPLOAD_TIMEOUT = 60000;

// ==================== RESUME ====================
// Per-call auth headers (the project pattern — there is no axios interceptor for auth).
import { supabase } from './supabase';

async function authHeader() {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}` };
}

/**
 * Upload a resume file (PDF, JPG, PNG, HEIC, HEIF). Replaces any prior resume.
 * Backend: POST /api/upload/resume
 * @param {File} file
 * @returns {Promise<{file_url: string, uploaded_at: string}>}
 */
export async function uploadResume(file) {
  const form = new FormData();
  form.append('file', file);
  // No Content-Type — Axios sets multipart/form-data with boundary automatically.
  const { data } = await api.post('/api/upload/resume', form, {
    headers: await authHeader(),
    timeout: UPLOAD_TIMEOUT,
  });
  return data;
}

/**
 * Parse the user's currently uploaded resume via Claude Vision.
 * Returns the extracted profile fields + a `_field_confidence` map.
 * Throws if no resume on file (404), parse failed (422), or rate-limited (429).
 * Backend: POST /api/profile/parse-resume
 */
export async function parseResume() {
  const { data } = await api.post('/api/profile/parse-resume', null, {
    headers: await authHeader(),
    timeout: UPLOAD_TIMEOUT,  // Claude can take 10-15s
  });
  return data;
}

/** Remove the user's resume file from their profile. */
export async function deleteResume() {
  const { data } = await api.delete('/api/profile/resume', { headers: await authHeader() });
  return data;
}

/** Get a 15-minute signed download URL for the user's own resume. */
export async function downloadResumeSignedUrl() {
  const { data } = await api.get('/api/profile/resume/download', { headers: await authHeader() });
  return data;
}

/**
 * Parse a resume WITHOUT requiring authentication. Used during the manual
 * signup flow to pre-fill the form before an account exists. The backend
 * does NOT store the file (parsed and discarded). IP-rate-limited (5/day).
 * @param {File} file
 * @returns {Promise<object>} extracted profile fields + _field_confidence
 */
export async function parseResumeAnonymous(file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post('/api/parse-resume-anonymous', form, {
    timeout: UPLOAD_TIMEOUT,
  });
  return data;
}

export default api;
